import { useQuery } from '@tanstack/react-query';
import { ethers } from 'ethers';
import { useContractQuery } from './useContractQuery';
import { useReadContracts } from './useContracts';

/** ERC-20 total supply (METH minted to date). */
export function useTotalSupply() {
  return useContractQuery<bigint>(['token', 'totalSupply']);
}

/** Balance of any address (returns 0n if address is undefined / disabled). */
export function useBalanceOf(address: string | null | undefined) {
  return useContractQuery<bigint>(
    ['token', 'balanceOf', address ?? '0x0000000000000000000000000000000000000000'],
    { enabled: !!address }
  );
}

/**
 * Per-snapshot historic balance — used for snapshot-weighted governance votes.
 * Note: parameter is snapshotId (despite the on-chain name "blockNumber").
 */
export function useBalanceOfAt(address: string | null | undefined, snapshotId: bigint | null | undefined) {
  return useContractQuery<bigint>(
    ['token', 'balanceOfAt', address ?? '0x0', snapshotId ?? 0n],
    { enabled: !!address && snapshotId != null && snapshotId > 0n }
  );
}

/** Per-snapshot historic total supply — denominator for vote weight share. */
export function useTotalSupplyAt(snapshotId: bigint | null | undefined) {
  return useContractQuery<bigint>(
    ['token', 'totalSupplyAt', snapshotId ?? 0n],
    { enabled: snapshotId != null && snapshotId > 0n }
  );
}

export type Holder = { address: string; balance: bigint };

/**
 * Derive the top-N METH holders by scanning Transfer events and aggregating
 * unique recipient addresses, then querying current balanceOf for each.
 *
 * Caveats — this is a frontend best-effort, not authoritative:
 *   · We scan only the last `lookbackBlocks` (default ~ 7 days on Sepolia).
 *     Holders who received their METH BEFORE that window won't appear
 *     unless they also moved tokens within the window.
 *   · Public RPCs throttle eth_getLogs to ~10k blocks; we chunk if needed.
 *   · Refresh interval is long (5 minutes) — holdings move slowly.
 *
 * For a production-grade list, mirror Transfer events into Supabase and
 * derive holders off-chain (Phase 2.5).
 */
export function useTopHolders(topN = 5, lookbackBlocks = 50_000) {
  const contracts = useReadContracts();
  return useQuery<Holder[], Error>({
    queryKey: ['metha', 'token', 'topHolders', topN, lookbackBlocks] as const,
    queryFn: async () => {
      const token = contracts.token;
      const provider = token.runner as ethers.Provider;
      const latest = await provider.getBlockNumber();
      const from = Math.max(0, latest - lookbackBlocks);

      // Aggregate unique recipient addresses across the scan window.
      // We chunk in 10k-block batches to stay under public-RPC limits.
      const addrs = new Set<string>();
      const STEP = 10_000;
      for (let start = from; start <= latest; start += STEP) {
        const end = Math.min(latest, start + STEP - 1);
        const filter = token.filters.Transfer();
        const events = await token.queryFilter(filter, start, end);
        for (const ev of events) {
          const args = (ev as ethers.EventLog).args;
          if (!args) continue;
          const to = String(args[1]);
          if (to && to !== ethers.ZeroAddress) addrs.add(to.toLowerCase());
        }
      }

      // balanceOf each candidate. Filter zeros, sort desc, slice top N.
      const balances = await Promise.all(
        Array.from(addrs).map(async (a) => ({
          address: ethers.getAddress(a),
          balance: (await token.balanceOf(a)) as bigint,
        }))
      );
      return balances
        .filter((h) => h.balance > 0n)
        .sort((a, b) => (b.balance > a.balance ? 1 : b.balance < a.balance ? -1 : 0))
        .slice(0, topN);
    },
    staleTime: 5 * 60_000,
    refetchInterval: 5 * 60_000,
  });
}
